import { createInput, defaultConfig } from '@formkit/vue';
import { createProPlugin, autocomplete, dropdown, taglist, currency } from '@formkit/pro';
import { addAsteriskPlugin } from '@/plugins/formkit/asterisk';
import { userLocale } from '@/utils/locale';
import { commissionRateAmount, noWhitespace, commissionable } from '@/utils/validators';
import DatePicker from '@/components/form/DatePicker.vue';
import EntityList from '@/components/form/entity-list/EntityList.vue';
import { CommissionType } from '@/constants/reservations.js';

const proPlugin = createProPlugin(import.meta.env.VITE_FORM_KIT_KEY, {
  autocomplete,
  currency,
  dropdown,
  taglist,
});

export default defaultConfig({
  rules: { commissionRateAmount, noWhitespace, commissionable },
  plugins: [proPlugin, addAsteriskPlugin],
  inputs: {
    entityList: createInput(EntityList, {
      props: {
        emptyText: {
          type: String,
        },
        nonEmptyText: {
          type: String,
        },
        addBtnText: {
          type: String,
        },
        onAdd: {
          type: Function,
        },
        onEdit: {
          type: Function,
        },
        canCopy: {
          type: Boolean,
          default: true,
        },
      },
    }),
    datePicker: createInput(DatePicker, {
      props: {
        range: {
          type: Boolean,
          default: false,
        },
        multiCalendars: {
          type: Boolean,
          default: false,
        },
        clearable: {
          type: Boolean,
          default: false,
        },
        disabled: {
          type: Boolean,
          default: false,
        },
      },
    }),
  },
  config: {
    locale: userLocale,
    classes: {
      // base styles
      outer: 'group w-full',
      wrapper: 'd-flex flex-col',
      label: 'label text-sm mb-1',
      asterisk: 'text-brand-600 -ml-1',
      placeholder: 'text-left text-gray-400',
      inner:
        'input w-full focus-within:outline-offset-0 focus-within:outline-primary focus-within:border-transparent group-data-[invalid]:border-red-500 group-data-[errors]:border-red-500 group-data-[disabled]:bg-gray-100 group-data-[disabled]:cursor-not-allowed',
      help: 'label text-sm mt-1 text-gray-500! text-wrap',
      message: 'validator-hint text-red-500',

      // styles for dropdown https://formkit.com/inputs/dropdown#sections
      selector:
        'w-full h-[calc(var(--size)-0.75rem)] flex items-center justify-between outline-0 cursor-pointer group-data-[disabled]:cursor-not-allowed group-data-[disabled]:text-gray-400',
      selections: 'flex absolute inset-0 justify-start group-data-[empty=true]:hidden',
      selectionWrapper:
        'flex items-center grow bg-base-100 group-data-[disabled]:bg-transparent group-data-[disabled]:text-gray-800 rounded-box group-data-[appearance=option]:px-[calc(0.25rem*3)]',

      // styles for taglist https://formkit.com/inputs/taglist#sections
      tags: 'flex flex-wrap items-center gap-1 flex-1 [&>input]:flex-1 [&>input]:w-auto',
      dropdownWrapper: 'left-0 bg-white border border-gray-200 shadow-lg rounded-box translate-y-1 empty:hidden',
      listbox: 'p-1.5',
      listboxButton: 'cursor-pointer',
      emptyMessage: 'px-2.5 py-1.5 text-gray-500 text-center',
    },
  },
  messages: {
    en: {
      validation: {
        commissionRateAmount({ args }) {
          if (args[0] === CommissionType.PERCENTAGE) {
            return 'Commission amount must be between 1 and 100%';
          }
          return 'Commission amount must be more than 1'; // FLAT
        },
        noWhitespace: 'Cannot contain spaces or tabs',
        commissionable({ args }) {
          if (args[0] === 'true') {
            return 'Commissionable reservation should have at least 1 commissionable rate code';
          }
          return 'Non-commissionable reservation should not have any commissionable rate codes';
        },
      },
    },
  },
});
